/**
 * providers/ninetailed/ninetailed-trait-sync.tsx
 *
 * Syncs the `isMember` trait into the visitor's Ninetailed profile. The member
 * signal is derived by {@link resolveIsMember} from `isLoggedIn` AND the
 * synchronously-cached `cachedInitials`, so a returning member is not briefly
 * reported as a non-member while the async `/api/auth/refresh` session restore is
 * still settling (which would flicker the banner from baseline to variant). The
 * effect is keyed on that derived signal so it re-fires only when it actually
 * changes. The uid is anonymous (empty string) and the payload contains only
 * `isMember` (no PII).
 *
 * Not gated here: `identify` is blocked centrally by the SDK's privacy plugin
 * until the visitor grants consent (see buildNinetailedOptions), so this always
 * calls `identify` and relies on the plugin to drop it pre-consent.
 *
 * This owns trait sync for ONGOING login-state changes only (keyed on the derived
 * member signal). The one-off re-sync needed the moment consent is first granted
 * mid-session is owned by `NinetailedConsentBridge`, which re-fires `identify`
 * AFTER the SDK's async feature-enable settles — firing here on the consent flip
 * would race that enable and fetch a profile without personalization features, so
 * the trait would not resolve any experience until a full reload.
 */

'use client';

import { useEffect } from 'react';
import { useNinetailed } from '@ninetailed/experience.js-next';

import { useUser } from '@/providers/UserProvider';
import { toMemberTrait } from '@/lib/ninetailed/toMemberTrait';
import { resolveIsMember } from '@/lib/ninetailed/resolveIsMember';

export function NinetailedTraitSync() {
  const { identify } = useNinetailed();
  const { isLoggedIn, cachedInitials } = useUser();
  const isMember = resolveIsMember(isLoggedIn, cachedInitials);

  useEffect(() => {
    identify('', toMemberTrait(isMember));
  }, [identify, isMember]);

  return null;
}
